import { useEffect, useState, type ReactNode } from "react";
import { BadgeCheck, Star, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { imageUrl } from "@/lib/image";
import { discountPercent, formatNaira } from "@/lib/money";

/* ── PriceTag ───────────────────────────────────────────────────────────── */
export interface PriceTagProps {
  kobo: number;
  oldKobo?: number | null;
  negotiable?: boolean;
  size?: "sm" | "md" | "lg";
  /** Keep everything on one line (cards): the old price is read to screen readers only. */
  singleLine?: boolean;
  className?: string;
}

/** <PriceTag kobo={45000000} oldKobo={52000000} negotiable /> — amounts are always kobo. */
export function PriceTag({ kobo, oldKobo, negotiable, size = "md", singleLine, className }: PriceTagProps) {
  const off = discountPercent(kobo, oldKobo);
  return (
    <div
      className={cn(
        "flex items-baseline gap-x-2",
        singleLine ? "min-w-0 flex-nowrap overflow-hidden whitespace-nowrap" : "flex-wrap gap-y-1",
        className,
      )}
    >
      <span className={cn("tabular shrink-0 font-bold text-ink", { sm: "text-[17px]", md: "text-xl", lg: "text-3xl" }[size])}>
        {formatNaira(kobo)}
      </span>
      {off > 0 && oldKobo && (
        <>
          {/* One-line cards have no room for the old price; the discount badge says it. */}
          <span className={cn("tabular text-sm text-ink-muted line-through", singleLine && "sr-only")}>
            <span className="sr-only">Was </span>
            {formatNaira(oldKobo)}
          </span>
          <Badge tone="magenta" className={cn(singleLine && "shrink-0")}>
            -{off}%
          </Badge>
        </>
      )}
      {negotiable && <Badge tone="neutral">Negotiable</Badge>}
    </div>
  );
}

/* ── Badge ──────────────────────────────────────────────────────────────── */
export type BadgeTone = "neutral" | "primary" | "cyan" | "magenta" | "success" | "warning" | "danger";

/** <Badge tone="success">Paid</Badge> — the label always carries the meaning, colour only reinforces it. */
export function Badge({
  tone = "neutral",
  children,
  className,
  icon,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        {
          neutral: "border-line bg-fg/[0.04] text-ink-soft",
          primary: "border-primary/40 bg-primary/15 text-[var(--primary-text)]",
          cyan: "border-cyan/40 bg-cyan/10 text-cyan",
          magenta: "border-magenta/40 bg-magenta/10 text-magenta",
          success: "border-success/40 bg-success/10 text-success",
          warning: "border-warning/40 bg-warning/10 text-warning",
          danger: "border-danger/40 bg-danger/10 text-danger",
        }[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** <VerifiedBadge /> — only for KYC tier-2 vendors. */
export function VerifiedBadge({ compact = false, className }: { compact?: boolean; className?: string }) {
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-xs font-medium text-cyan", className)}
      title="Verified vendor (ID + bank checked)"
    >
      <BadgeCheck className="size-4 drop-shadow-[0_0_6px_rgb(var(--rgb-accent-cyan)/0.7)]" aria-hidden />
      {compact ? <span className="sr-only">Verified vendor</span> : "Verified"}
    </span>
  );
}

/* ── RatingStars ────────────────────────────────────────────────────────── */
/** <RatingStars value={4.6} count={128} /> */
export function RatingStars({
  value,
  count,
  size = "sm",
  className,
}: {
  value: number;
  count?: number;
  size?: "sm" | "md";
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(5, value));
  const px = size === "sm" ? "size-3.5" : "size-5";
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <span className="relative inline-flex" role="img" aria-label={`Rated ${clamped.toFixed(1)} out of 5`}>
        <span className="flex text-line">
          {Array.from({ length: 5 }, (_, i) => (
            <Star key={i} className={cn(px, "fill-current")} aria-hidden />
          ))}
        </span>
        <span className="absolute inset-0 flex overflow-hidden text-warning" style={{ width: `${(clamped / 5) * 100}%` }}>
          {Array.from({ length: 5 }, (_, i) => (
            <Star key={i} className={cn(px, "shrink-0 fill-current")} aria-hidden />
          ))}
        </span>
      </span>
      <span className="tabular text-xs text-ink-soft">{clamped.toFixed(1)}</span>
      {count !== undefined && <span className="text-xs text-ink-muted">({count.toLocaleString("en-NG")})</span>}
    </span>
  );
}

/* ── Chip ───────────────────────────────────────────────────────────────── */
/** <Chip selected={on} onClick={toggle}>128GB</Chip> or <Chip onRemove={clear}>Lagos</Chip> */
export function Chip({
  children,
  selected,
  onClick,
  onRemove,
  className,
}: {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  className?: string;
}) {
  const base = cn(
    "inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm transition",
    selected
      ? "border-primary bg-primary/20 text-ink shadow-glow-1"
      : "border-line bg-fg/[0.03] text-ink-soft hover:border-[var(--border-glow)] hover:text-ink",
    className,
  );
  if (onRemove) {
    return (
      <span className={base}>
        {children}
        <button
          type="button"
          onClick={onRemove}
          className="-mr-1 grid size-6 place-items-center rounded-full hover:bg-fg/10"
          aria-label={`Remove ${typeof children === "string" ? children : "filter"}`}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-pressed={selected} className={base}>
      {children}
    </button>
  );
}

/* ── Avatar ─────────────────────────────────────────────────────────────── */
/** Stable hue (0–359) from an id, so a store's initials avatar keeps its colour everywhere. */
export function hueFor(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % 360;
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

const AVATAR_SIZES = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-16 text-lg",
  xl: "size-24 text-2xl md:size-32 md:text-3xl",
} as const;
const AVATAR_PX = { xs: 48, sm: 64, md: 96, lg: 160, xl: 320 } as const;

/**
 * <Avatar name="Ada Obi" src={user.avatar_thumb} size="md" seed={user.id} />
 * Falls back to initials on a violet→cyan gradient (tinted by `seed`) when there's no photo or it fails;
 * photos fade in over the initials (blur-up), lazy-loaded.
 */
export function Avatar({
  name,
  src,
  size = "md",
  seed,
  online,
  className,
}: {
  name: string;
  src?: string | null;
  size?: keyof typeof AVATAR_SIZES;
  seed?: string;
  /** Presence dot (green when true, none when undefined). */
  online?: boolean;
  className?: string;
}) {
  const initials = initialsOf(name);
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setBroken(false);
    setLoaded(false);
  }, [src]);
  const hue = seed ? hueFor(seed) : null;
  const style =
    hue === null
      ? undefined
      : {
          backgroundImage: `linear-gradient(135deg, hsl(${(252 + (hue % 40) - 20 + 360) % 360} 85% 62%), hsl(${(189 + (hue % 50) - 25 + 360) % 360} 82% 48%))`,
        };
  const px = AVATAR_PX[size];
  return (
    <span className={cn("relative inline-flex shrink-0 rounded-full", AVATAR_SIZES[size], className)}>
      <span
        style={style}
        className={cn(
          "relative grid size-full place-items-center overflow-hidden rounded-full font-semibold text-white ring-1 ring-fg/10",
          !style && "bg-gradient-to-br from-primary/60 to-cyan/40",
        )}
      >
        <span aria-hidden className={cn("transition-opacity duration-300", src && !broken && loaded && "opacity-0")}>
          {initials || "?"}
        </span>
        {src && !broken && (
          <img
            src={imageUrl(src, px, { crop: "fill", height: px })}
            alt=""
            className={cn("absolute inset-0 size-full object-cover transition-opacity duration-300", loaded ? "opacity-100" : "opacity-0")}
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
          />
        )}
        <span className="sr-only">{name}</span>
      </span>
      {online !== undefined && (
        <span
          aria-hidden
          className={cn(
            "absolute bottom-0 right-0 rounded-full ring-2 ring-void",
            size === "xs" || size === "sm" ? "size-2.5" : "size-3",
            online ? "bg-success" : "hidden",
          )}
        />
      )}
    </span>
  );
}

/* ── Skeleton ───────────────────────────────────────────────────────────── */
/** <Skeleton className="h-4 w-32" /> — shimmering placeholder while loading. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("relative block overflow-hidden rounded-lg bg-fg/[0.05]", className)}>
      <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-fg/[0.07] to-transparent" />
    </span>
  );
}

/* ── EmptyState ─────────────────────────────────────────────────────────── */
/** <EmptyState icon={<Package />} title="No orders yet" action={<ButtonLink to="/">Start shopping</ButtonLink>} /> */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-14 text-center", className)}>
      {icon && (
        <div className="mb-5 grid size-16 place-items-center rounded-2xl border border-[var(--border-glow)] bg-elevated text-[var(--primary-text)] shadow-glow-2 [&>svg]:size-7">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && <p className="mt-2 max-w-sm text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/* ── Logo ───────────────────────────────────────────────────────────────── */
export function Logo({ className, compact = false }: { className?: string; compact?: boolean }) {
  // The 3D logo kit: the orb mark alone, or the horizontal lockup in its light / dark version.
  if (compact)
    return (
      <span className={cn("inline-flex items-center", className)}>
        <img
          src="/brand/logo-mark.webp"
          alt="RealityStore"
          data-logo-mark
          width={36}
          height={36}
          decoding="async"
          className="size-9 object-contain"
        />
      </span>
    );
  return (
    <span className={cn("inline-flex items-center", className)}>
      <img
        src="/brand/logo-horizontal-light.webp"
        alt="RealityStore"
        data-logo-mark
        width={461}
        height={72}
        decoding="async"
        className="h-8 w-auto dark:hidden"
      />
      <img
        src="/brand/logo-horizontal-dark.webp"
        alt="RealityStore"
        data-logo-mark
        width={461}
        height={72}
        decoding="async"
        className="hidden h-8 w-auto dark:block"
      />
    </span>
  );
}
